import { Component, Suspense, useEffect, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { navigation, network } from '@good-season/platform';
import { Button, ErrorBoundary, SUPPORT_CODE_LEAD, diagnosticFor } from '@good-season/ui';

import { useSignOut } from '../api/useSignOut.js';
import { AppShell } from '../components/AppShell.js';
import { reportBoundary } from '../help/auto-report.js';

const reportRouteCrash = reportBoundary('RT');

/**
 * Wraps a screen that is loaded only when it is opened (ADR-0096).
 *
 * Three things, all there so a route that is not in the first download never
 * shows a blank page or a dead end:
 *
 * - While the chunk arrives, a designed placeholder (the same grey bars
 *   `CoachHome` uses), not a spinner. `shell` keeps the app's header and
 *   navigation on screen so the page does not jump when the screen lands;
 *   `bare` is for the wizards, which are outside the shell by design.
 * - After `SLOW_AFTER_MS` with no chunk, the placeholder says so and offers
 *   Try again and Home, because on the wizards nothing else on screen is
 *   clickable.
 * - If the chunk cannot be fetched (offline before the service worker cached
 *   it, or a purged hash), the dynamic import rejects while React renders and
 *   `LoadBoundary` shows copy that says the screen could not be loaded, with
 *   Try again (reload) and a way Home. `RT` is the diagnostic prefix. Keyed by
 *   pathname so moving to another route mounts a fresh boundary.
 *
 * Never used for the scorebook: it must open with no signal.
 */
export const SLOW_AFTER_MS = 15_000;

/**
 * What a rejected dynamic import is turned into by `loadChunk`, so
 * `LoadBoundary` can tell "the file would not download" from "the screen
 * crashed". Only this reaches the "could not be loaded" copy.
 */
export class ChunkLoadError extends Error {
  constructor(cause: unknown) {
    super('A screen chunk failed to load.', { cause });
    this.name = 'ChunkLoadError';
  }
}

/** Wrap a lazy `import()` so its failure is tagged as a chunk-load failure. */
export async function loadChunk<T>(importer: () => Promise<T>): Promise<T> {
  try {
    return await importer();
  } catch (error) {
    throw new ChunkLoadError(error);
  }
}

export function LazyScreen({
  chrome,
  children,
}: {
  readonly chrome: 'shell' | 'bare';
  readonly children: ReactNode;
}): ReactNode {
  const { pathname } = useLocation();
  return (
    // Outer: anything that is not a chunk-load failure (a screen that loaded
    // and then crashed while rendering) gets the generic "This screen stopped".
    <ErrorBoundary key={pathname} surface="RT" fills="screen" onError={reportRouteCrash}>
      <LoadBoundary>
        <Suspense fallback={<RoutePlaceholder chrome={chrome} />}>{children}</Suspense>
      </LoadBoundary>
    </ErrorBoundary>
  );
}

/** Home, keeping `?team=` (the selected team lives only in the query string). */
function useHome(): () => void {
  const navigate = useNavigate();
  const { search } = useLocation();
  return () => {
    const team = new URLSearchParams(search).get('team');
    void navigate({
      pathname: '/',
      search: team === null ? '' : `?${new URLSearchParams({ team }).toString()}`,
    });
  };
}

function Actions(): ReactNode {
  const goHome = useHome();
  return (
    <div className="gs-error__actions">
      <Button
        onClick={() => {
          navigation.restart();
        }}
      >
        Try again
      </Button>
      <Button tone="ghost" onClick={goHome}>
        Back to Home
      </Button>
    </div>
  );
}

function LoadFailure({ error }: { readonly error: unknown }): ReactNode {
  const offline = !network.isOnline();
  return (
    <main>
      <section className="gs-ui gs-error" role="alert">
        <h1 className="gs-error__title">This screen could not be loaded</h1>
        <p className="gs-error__body">
          {offline
            ? 'This screen needs a connection the first time it opens. Connect to the internet, then try again. Anything already saved is safe.'
            : 'The connection may have dropped while it loaded. Try again, and if it stops twice send this code along. Anything already saved is safe.'}
        </p>
        <hr className="gs-error__rule" />
        <p className="gs-diagnostic">
          <span>{SUPPORT_CODE_LEAD}</span>{' '}
          <span className="gs-diagnostic__code">{diagnosticFor(error, 'RT')}</span>
        </p>
        <Actions />
      </section>
    </main>
  );
}

interface LoadBoundaryState {
  readonly caught: { readonly value: unknown } | null;
}

/**
 * Catches a chunk that failed to load, and only that. Anything else is thrown
 * again from render so the `ErrorBoundary` above it shows the generic crash
 * fallback. A wrapper so a thrown null still counts.
 */
class LoadBoundary extends Component<{ readonly children: ReactNode }, LoadBoundaryState> {
  override state: LoadBoundaryState = { caught: null };

  static getDerivedStateFromError(error: unknown): LoadBoundaryState {
    return { caught: { value: error } };
  }

  override render(): ReactNode {
    const { caught } = this.state;
    if (caught === null) return this.props.children;
    if (!(caught.value instanceof ChunkLoadError)) throw caught.value;
    return <LoadFailure error={caught.value} />;
  }
}

function SlowNotice(): ReactNode {
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => {
      setSlow(true);
    }, SLOW_AFTER_MS);
    return () => {
      clearTimeout(timer);
    };
  }, []);
  if (!slow) return null;
  return (
    <div data-route-slow>
      <p className="team__body" role="status">
        This is taking longer than usual. Check your connection.
      </p>
      <Actions />
    </div>
  );
}

/**
 * `asHeading` outside the shell: the bare wizards have no header of their own
 * until the chunk arrives, and a page needs one level-one heading (axe
 * `page-has-heading-one`). Inside the shell the header already carries it.
 *
 * `data-route-placeholder` is the marker `e2e/accessibility.spec.ts` waits to
 * see gone before it scans, so a scan is of the screen and not its placeholder.
 */
function Bars({ asHeading }: { readonly asHeading: boolean }): ReactNode {
  return (
    <div className="team" data-route-placeholder>
      <div role="status" aria-live="polite">
        {asHeading ? (
          <h1 className="team__eyebrow">One moment.</h1>
        ) : (
          <p className="team__eyebrow">One moment.</p>
        )}
        <div className="home__placeholder" aria-hidden="true">
          <span className="home__bar" />
          <span className="home__bar home__bar--long" />
          <span className="home__bar" />
        </div>
      </div>
      <SlowNotice />
    </div>
  );
}

export function RoutePlaceholder({ chrome }: { readonly chrome: 'shell' | 'bare' }): ReactNode {
  const signOut = useSignOut();
  if (chrome === 'shell') {
    // The same sign-out the screens use, so the visible control works.
    return (
      <AppShell onSignOut={() => void signOut()}>
        <Bars asHeading={false} />
      </AppShell>
    );
  }
  return (
    <main className="shell__main">
      <Bars asHeading />
    </main>
  );
}
